import React, { useEffect, useState } from "react"
import Header from "../../../components/navigation/Header"
import Footer from "../../../components/navigation/Footer"
import { faArrowLeftLong } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import GetCSFR from "../../../hooks/userAuthentication/GetCSFR"
import GetWindowSize from "../../../hooks/general/GetWindowSize"

export default function Invite(){
    const navigate = useNavigate()
    const csrfToken = GetCSFR({ name: "csrftoken" })
    const [personalReferralCode, setPersonalReferralCode] = useState<string>("")
    const [redeemStatus, setRedeemStatus] = useState<boolean>(false)
    const windowSize = GetWindowSize()
    const [errorMessage, setErrorMessage] = useState<string>("")
    const [externalReferralCode, setExternalReferralCode] = useState<string>("")
    
    useEffect(()=>{
        document.title = 'SkillJa - Invite Coaches!'
        // API call for referral code
        axios.get(`${process.env.REACT_APP_SKILLJA_URL}/referral/get_referral_code/`,{
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        })
        .then(res => {
            if(res.status === 200){
                setPersonalReferralCode(res.data.code)
            }
            else{
                setPersonalReferralCode('unavailable')
            }
        })
        .catch(error =>{console.error(error)})
    },[])

    function redeemCode(){
        // API call to verify referral code
        setErrorMessage("")
        axios.post(`${process.env.REACT_APP_SKILLJA_URL}/referral/redeem_code/`, {code: externalReferralCode}, {
            headers: {
                'X-CSRFToken': csrfToken,
                'Content-Type': 'application/json'
            },
            withCredentials: true
        })
        .then(res => {
            if(res.status === 201){
                setRedeemStatus(true)
                const timer = setTimeout(() => {
                    setRedeemStatus(false)
                }, 1500)
                return ()=> clearTimeout
            }
            else if (res.status === 204) {
                setRedeemStatus(false)
                setErrorMessage('Invalid referral code!')
            }
            else if (res.status === 200){
                setRedeemStatus(false)
                setErrorMessage('Code cannot be redeemed!')
            }
        })
        .catch(error =>{console.error(error)})
    }

    return (
        <>
            <Header useCase="protected" />
            <section className="font-kulim flex flex-col justify-start items-start py-2 px-4 lg:px-20 mx-auto mt-10 mb-36">
                <FontAwesomeIcon 
                    icon={faArrowLeftLong} 
                    className="text-3xl hover:text-main-green-500 cursor-pointer mb-8"
                    onClick={()=> navigate('../')}
                />
                <h2 className="text-3xl font-kulim mb-3">
                    Earn rewards for every coach or athlete you refer to SkillJa
                </h2>
                <p className="max-w-4xl">
                    Invite a coach or athlete to join SkillJa and earn rewards once they book their first lesson or sell their first session.
                    Referrals activate only after real bookings/signups. Minimum $25 session value applies. Maximum 3 redemptions per month. A completed Stripe account is required to receive cash rewards.
                </p>    
                <p className="mt-10 underline font-semibold">When you refer an Athlete:</p>
                <ul className="list-disc list-inside space-y-1">
                    <li>1 referral → $10 off your next lesson</li>
                    <li>2 referrals → $25 off your next lesson</li>
                    <li>When an athlete books a lesson → $10 cash</li>
                </ul>
                <p className="mt-6 underline font-semibold">When you refer a Coach:</p>
                <ul className="list-disc list-inside space-y-1">
                    <li>1 referral → $10 cash</li>
                    <li>2 referrals → $25 cash</li>
                    <li>3+ referrals → Featured on SkillJa's front page</li>
                </ul>
                <section className="flex flex-wrap mt-16">
                    <div className="border border-main-grey-100 w-72 md:w-80 rounded-2xl bg-white px-6 py-4 mb-6 md:mb-0">
                        <div className="flex flex-col">
                            <p className="font-semibold">Your referral code</p>
                            <div className="flex bg-white border font-semibold border-main-gray-200 rounded-2xl p-3 mt-3 mb-6">
                                <p className="my-auto ml-0 mr-auto text-lg">
                                    {personalReferralCode || 'unavailable'}
                                </p>
                                <button 
                                    type="button"
                                    className="text-sm py-1 px-4 bg-main-grey-100 w-fit ml-auto mr-0 rounded-xl font-semibold cursor-pointer hover:bg-main-grey-200" 
                                    aria-label="Copy code"
                                    disabled={personalReferralCode.length === 0 }
                                    onClick={()=>{navigator.clipboard.writeText(personalReferralCode); alert(`Copied code ${personalReferralCode}`)}}
                                >
                                    Copy
                                </button>
                            </div>
                            <p>
                                Click copy to share your personal referral code with others.
                            </p>
                        </div>
                    </div>
                    {windowSize.width > 600 && <div className="bg-main-grey-100 rounded-full w-1 mx-10 lg:mx-16"></div>}
                    <div className="border border-main-grey-100 w-72 md:w-80 rounded-2xl bg-white px-6 py-4">
                        <div className="flex flex-col">
                            <p className="font-semibold">
                                Redeem a referral code
                            </p>
                            <input 
                                type="text" 
                                className="form-input border-main-grey-100 px-3 mt-3 mb-3" 
                                value={externalReferralCode}
                                onChange={(e)=>setExternalReferralCode(e.target.value)}
                            />
                            {redeemStatus && !errorMessage ? 
                                <p className="text-main-green-600 mx-auto font-semibold mt-3">Success!</p>
                            :
                                <button 
                                    type="button"
                                    className="form-btn" 
                                    onClick={()=>redeemCode()} 
                                    aria-label="redeem referral code"
                                    disabled={externalReferralCode.length === 0}
                                >
                                    Redeem Code
                                </button>
                            }
                            {errorMessage && 
                                <p className="text-sm text-red-600 font-semibold mt-5 mx-auto">
                                    {errorMessage}
                                </p>
                            }
                        </div>
                    </div>
                </section>
            </section>
            <Footer />
        </>
    )
}